import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle, AlertCircle, Info, CheckCircle, TrendingUp, Copy, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { GameSelector, GameBadge } from "@/components/GameSelector";

export default function PrioritySummary() {
  const { t } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState("STAR_ATLAS_UE5");

  const fetchSummary = async (game) => {
    try {
      setLoading(true);
      const gameParam = game ? `?game=${game}` : '';
      const response = await axios.get(`${API}/priority-summary${gameParam}`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(selectedGame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame]);

  const handleGameChange = (game) => {
    setSelectedGame(game);
  };

  const copyToClipboard = () => {
    if (!summary) return;

    const gameName = selectedGame === "STAR_ATLAS_HOLOSIM" ? "HOLOSIM" : "UE5";
    let text = `=== BUGNEXUS PRIORITY SUMMARY (${gameName}) ===\n\n`;

    if (summary.critical.length > 0) {
      text += "CRITICAL (Fix immediately):\n";
      summary.critical.forEach((bug) => {
        text += `- [${bug.display_code || bug.id.slice(0,8)}] [${bug.category || 'N/A'}] [${bug.classification}] ${bug.title}${bug.duplicate_count > 0 ? ` (${bug.duplicate_count} duplicates)` : ""}${bug.reopened ? " [REOPENED]" : ""}\n`;
      });
      text += "\n";
    }

    if (summary.high.length > 0) {
      text += "HIGH (Next sprint):\n";
      summary.high.forEach((bug) => {
        text += `- [${bug.display_code || bug.id.slice(0,8)}] [${bug.category || 'N/A'}] [${bug.classification}] ${bug.title}${bug.duplicate_count > 0 ? ` (${bug.duplicate_count} duplicates)` : ""}${bug.reopened ? " [REOPENED]" : ""}\n`;
      });
      text += "\n";
    }

    if (summary.medium.length > 0) {
      text += "MEDIUM (Backlog - monitor frequency):\n";
      summary.medium.forEach((bug) => {
        text += `- [${bug.display_code || bug.id.slice(0,8)}] [${bug.category || 'N/A'}] [${bug.classification}] ${bug.title}\n`;
      });
      text += "\n";
    }

    if (summary.low.length > 0) {
      text += "LOW (Polish / non-blocking):\n";
      summary.low.forEach((bug) => {
        text += `- [${bug.display_code || bug.id.slice(0,8)}] [${bug.category || 'N/A'}] [${bug.classification}] ${bug.title}\n`;
      });
      text += "\n";
    }

    if (summary.high_frequency_issues.length > 0) {
      text += "HIGH FREQUENCY ISSUES:\n";
      summary.high_frequency_issues.forEach((issue) => {
        text += `- ${issue}\n`;
      });
    }

    navigator.clipboard.writeText(text);
    toast.success(t('common.success'));
  };

  const PrioritySection = ({ title, bugs, icon: Icon, color, description }) => (
    <Card className={`bg-card border-border/40 ${bugs.length > 0 ? 'hover:border-primary/30' : ''} transition-colors`}>
      <CardHeader className={`border-b border-border/40 ${color} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5" />
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider">
              {title}
            </CardTitle>
          </div>
          <span className="font-mono text-2xl font-bold">{bugs.length}</span>
        </div>
        <p className="text-xs font-mono mt-1 opacity-70">{description}</p>
      </CardHeader>
      <CardContent className="p-0">
        {bugs.length > 0 ? (
          <div className="divide-y divide-border/40">
            {bugs.map((bug) => (
              <Link
                key={bug.id}
                to={`/bugs/${bug.id}`}
                className="block p-4 hover:bg-muted/30 transition-colors"
                data-testid={`priority-bug-${bug.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Display Code */}
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-mono text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm border border-primary/20">
                        {bug.display_code || `#${bug.id.slice(0, 8)}`}
                      </span>
                      {bug.category && (
                        <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
                          <Gamepad2 className="w-3 h-3" />
                          {bug.category}
                        </span>
                      )}
                      {bug.reopened && (
                        <span className="px-1.5 py-0.5 bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-none font-mono text-[10px] uppercase">
                          Reopened
                        </span>
                      )}
                    </div>
                    <p className="font-body text-sm text-foreground">{bug.title}</p>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      {bug.classification}
                    </p>
                  </div>
                  {bug.duplicate_count > 0 && (
                    <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-none font-mono text-[10px] uppercase flex-shrink-0">
                      {bug.duplicate_count}x
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center">
            <p className="text-muted-foreground font-mono text-sm">{t('priority.noBugsInCategory')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="priority-summary-page">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground">{t('priority.title')}</h1>
            <div className="cyber-line mt-2" />
            <p className="text-muted-foreground mt-2 font-body text-sm md:text-base">{t('priority.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => fetchSummary(selectedGame)} className="font-heading text-xs" data-testid="refresh-priority-btn">
              <RefreshCw className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{t('priority.refresh')}</span>
            </Button>
            <Button size="sm" onClick={copyToClipboard} className="font-heading font-bold uppercase tracking-wider text-xs" data-testid="copy-summary-btn">
              <Copy className="w-4 h-4 mr-1" />
              {t('priority.copySummary')}
            </Button>
          </div>
        </div>
      </div>

      {/* Game Selector */}
      <div className="mb-6">
        <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-3 block">
          Select Game
        </label>
        <GameSelector 
          selectedGame={selectedGame} 
          onGameChange={handleGameChange}
          size="large"
        />
      </div>

      {/* High Frequency Issues Alert */}
      {summary?.high_frequency_issues && summary.high_frequency_issues.length > 0 && (
        <Card className="bg-orange-500/5 border-orange-500/20 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-bold text-orange-400 uppercase tracking-wider text-sm">{t('priority.highFrequencyIssues')}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {summary.high_frequency_issues.map((issue, i) => (
                    <span key={i} className="px-3 py-1 bg-orange-500/10 border border-orange-500/20 rounded-none font-mono text-xs text-orange-300">
                      {issue}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Priority Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <PrioritySection
          title={t('priority.criticalTitle')}
          bugs={summary?.critical || []}
          icon={AlertTriangle}
          color="bg-red-500/10 text-red-400"
          description={t('priority.criticalDesc')}
        />
        <PrioritySection
          title={t('priority.highTitle')}
          bugs={summary?.high || []}
          icon={AlertCircle}
          color="bg-orange-500/10 text-orange-400"
          description={t('priority.highDesc')}
        />
        <PrioritySection
          title={t('priority.mediumTitle')}
          bugs={summary?.medium || []}
          icon={Info}
          color="bg-yellow-500/10 text-yellow-400"
          description={t('priority.mediumDesc')}
        />
        <PrioritySection
          title={t('priority.lowTitle')}
          bugs={summary?.low || []}
          icon={CheckCircle}
          color="bg-blue-500/10 text-blue-400"
          description={t('priority.lowDesc')}
        />
      </div>
    </div>
  );
}
